import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { vehicleApi } from '../services/api';
import { VehicleSearchParams } from '../types/api';
import VehicleCard from '../components/VehicleCard';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const SearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchFilters, setSearchFilters] = useState<VehicleSearchParams>({
    page: 1,
    page_size: 20,
    sort_by: 'auction_date',
    sort_order: 'desc'
  });

  // Get search query from URL
  const searchQuery = searchParams.get('q') || '';

  // Update search filters when URL changes
  useEffect(() => {
    const filters: VehicleSearchParams = {
      keyword: searchQuery,
      page: searchParams.get('page') ? parseInt(searchParams.get('page')!) : 1,
      page_size: 20,
      sort_by: searchParams.get('sort_by') || 'auction_date',
      sort_order: (searchParams.get('sort_order') as 'asc' | 'desc') || 'desc'
    };

    setSearchFilters(filters);
  }, [searchQuery, searchParams]);

  // Fetch search results
  const { data: searchResults, isLoading, error } = useQuery(
    ['search', searchFilters],
    () => vehicleApi.searchVehicles(searchFilters),
    {
      enabled: !!searchQuery,
      keepPreviousData: true,
    }
  );

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const handleSortChange = (sortBy: string, sortOrder: 'asc' | 'desc') => {
    const params = new URLSearchParams(searchParams);
    params.set('sort_by', sortBy);
    params.set('sort_order', sortOrder);
    params.delete('page'); // Reset to page 1
    setSearchParams(params);
  };

  if (!searchQuery) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No search query</h3>
          <p className="mt-1 text-sm text-gray-500">Please enter a search term to find vehicles.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Search Results</h1>
              <p className="mt-2 text-gray-600">
                {isLoading ? (
                  'Searching...'
                ) : searchResults ? (
                  <>
                    {searchResults.total} results for "{searchQuery}"
                  </>
                ) : (
                  `Searching for "${searchQuery}"`
                )}
              </p>
            </div>
            
            {searchResults && searchResults.total > 0 && (
              <div className="mt-4 sm:mt-0">
                <select
                  value={`${searchFilters.sort_by}-${searchFilters.sort_order}`}
                  onChange={(e) => {
                    const [sortBy, sortOrder] = e.target.value.split('-');
                    handleSortChange(sortBy, sortOrder as 'asc' | 'desc');
                  }}
                  className="input-field w-auto"
                >
                  <option value="auction_date-desc">Auction Date (Newest)</option>
                  <option value="auction_date-asc">Auction Date (Oldest)</option>
                  <option value="year-desc">Year (Newest)</option>
                  <option value="year-asc">Year (Oldest)</option>
                  <option value="make-asc">Make (A-Z)</option>
                  <option value="make-desc">Make (Z-A)</option>
                  <option value="current_bid-desc">Bid (Highest)</option>
                  <option value="current_bid-asc">Bid (Lowest)</option>
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <div className="text-red-600 mb-2">
              <MagnifyingGlassIcon className="w-12 h-12 mx-auto mb-4" />
              <p>Search failed. Please try again.</p>
            </div>
          </div>
        )}

        {/* Search Results */}
        {searchResults && !isLoading && (
          <>
            {searchResults.items.length > 0 ? (
              <>
                {/* Results Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                  {searchResults.items.map((vehicle) => (
                    <VehicleCard key={vehicle.id} vehicle={vehicle} />
                  ))}
                </div>

                {/* Pagination */}
                <Pagination
                  currentPage={searchResults.page}
                  totalPages={searchResults.total_pages}
                  onPageChange={handlePageChange}
                />
              </>
            ) : (
              <div className="text-center py-12">
                <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No results found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Try different keywords or check your spelling.
                </p>
                <div className="mt-6">
                  <a
                    href="/vehicles"
                    className="btn-primary"
                  >
                    Browse All Vehicles
                  </a>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchPage;